// BookingInfoModal — hero-triggered informative modal on /booking.
//
// Opened by the "TU VIAJE EMPIEZA CON UNA CONVERSACIÓN" secondary CTA
// in the booking hero. Sets guest expectations BEFORE they scroll
// into the planner: five typed request families they can send us
// (single stay · multi-property route · bespoke experiences · add-on
// services · full custom trip), so they know the planner form
// naturally accommodates ALL of them and there is no wrong way to
// start.
//
// Design principles:
//   • Full-viewport dark overlay (matches DirectionsDialog) with
//     escape/backdrop-close + focus-trap-lite.
//   • Portalled to document.body so it lives above the sticky
//     summary, floating audio player and language switcher.
//   • Fully i18n (EN · FR · ES) via the shared `pick()` helper.

import { useEffect } from "react";
import { createPortal } from "react-dom";
import {
  X, Bed, Route as RouteIcon, Sparkles, Car, Compass, ArrowRight,
} from "lucide-react";

const pick = (dict, lang) => (typeof dict === "string" ? dict : (dict?.[lang] || dict?.en || ""));

const COPY = {
  overline: {
    en: "How a booking request works",
    fr: "Comment fonctionne une demande",
    es: "Cómo funciona una petición de reserva",
  },
  title: {
    en: "Your trip begins with a conversation",
    fr: "Votre voyage commence par une conversation",
    es: "Tu viaje empieza con una conversación",
  },
  intro_lead: {
    en: "At Grup Xaluca every enquiry is handled with personal attention so we can bring you the best possible proposal. Our team studies your preferences, coordinates availability across the selected properties and experiences, and designs a proposal shaped to the way you travel.",
    fr: "Chez Grup Xaluca, chaque demande est traitée de façon personnalisée afin de vous offrir la meilleure proposition possible. Notre équipe étudie vos préférences, coordonne la disponibilité des hébergements et des expériences retenus, et conçoit une proposition adaptée à votre façon de voyager.",
    es: "En Grup Xaluca cada solicitud se gestiona de forma personalizada para ofrecerte la mejor propuesta posible. Nuestro equipo estudia tus preferencias, coordina la disponibilidad de los alojamientos y experiencias seleccionados y diseña una propuesta adaptada a tu forma de viajar.",
  },
  intro_response: {
    en: "Within 48 hours you will receive a personalised proposal with the itinerary, the accommodations, the recommended experiences and everything you need to begin planning your trip.",
    fr: "Sous 48 heures maximum, vous recevrez une proposition personnalisée avec l'itinéraire, les hébergements, les expériences recommandées et toutes les informations nécessaires pour commencer à planifier votre voyage.",
    es: "En un plazo máximo de 48 horas recibirás una propuesta personalizada con el itinerario, los alojamientos, las experiencias recomendadas y toda la información necesaria para comenzar a planificar tu viaje.",
  },
  transition: {
    en: "From the planner you can request:",
    fr: "Depuis le planificateur, vous pouvez demander :",
    es: "Desde el planificador podrás solicitar:",
  },
  outro: {
    en: "There is no wrong way to start — write what you have (a hotel name, a date, an idea) and we take it from there.",
    fr: "Il n'y a pas de mauvaise façon de commencer — écrivez ce que vous avez (un nom d'hôtel, une date, une idée) et nous prenons le relais.",
    es: "No hay una forma equivocada de empezar — escríbenos lo que tengas (un nombre de hotel, una fecha, una idea) y nosotros nos encargamos del resto.",
  },
  cta: {
    en: "Open the planner",
    fr: "Ouvrir le planificateur",
    es: "Abrir el planificador",
  },
  close: { en: "Close", fr: "Fermer", es: "Cerrar" },
};

const REQUEST_TYPES = [
  {
    key: "single",
    icon: Bed,
    title: {
      en: "One or several accommodations",
      fr: "Un ou plusieurs hébergements",
      es: "Uno o varios alojamientos",
    },
    desc: {
      en: "Book a stay at any of our kasbah-inspired hotels, character riads, private desert camps or mountain refuges — or combine several properties in a single itinerary.",
      fr: "Réservez un séjour dans l'un de nos hôtels inspirés des kasbahs, riads de caractère, campements privés au désert ou refuges de montagne — ou combinez plusieurs hébergements dans un même itinéraire.",
      es: "Reserva una estancia en cualquiera de nuestros hoteles inspirados en kasbahs, riads con encanto, campamentos privados en el desierto o refugios de montaña, o combina varios alojamientos en un mismo itinerario.",
    },
  },
  {
    key: "route",
    icon: RouteIcon,
    title: {
      en: "A route through the south of Morocco",
      fr: "Un itinéraire à travers le sud du Maroc",
      es: "Una ruta por el sur de Marruecos",
    },
    desc: {
      en: "Design a fully personalised itinerary combining different destinations. Our team will prepare a balanced route based on your dates, the time available and the travel pace you prefer.",
      fr: "Composez un parcours entièrement personnalisé en combinant différentes destinations. Notre équipe préparera un itinéraire équilibré selon vos dates, le temps disponible et le rythme de voyage que vous préférez.",
      es: "Diseña un recorrido completamente personalizado combinando diferentes destinos. Nuestro equipo preparará un itinerario equilibrado según tus fechas, el tiempo disponible y el ritmo de viaje que prefieras.",
    },
  },
  {
    key: "experiences",
    icon: Sparkles,
    title: {
      en: "Personalised experiences",
      fr: "Expériences personnalisées",
      es: "Experiencias personalizadas",
    },
    desc: {
      en: "Round out your stay with camel rides, 4×4 outings, local gastronomy, wellness rituals, hiking, adventure activities or experiences designed exclusively for you.",
      fr: "Complétez votre séjour avec des balades à dromadaire, sorties en 4×4, gastronomie locale, rituels bien-être, randonnées, activités d'aventure ou expériences conçues exclusivement pour vous.",
      es: "Completa tu estancia con paseos en dromedario, rutas en 4×4, gastronomía local, bienestar, senderismo, actividades de aventura o experiencias diseñadas exclusivamente para ti.",
    },
  },
  {
    key: "add-ons",
    icon: Car,
    title: {
      en: "Transfers and additional services",
      fr: "Transferts et services complémentaires",
      es: "Traslados y servicios adicionales",
    },
    desc: {
      en: "We arrange airport-to-hotel transfers, private vehicles, drivers, local guides and every service needed for your journey to flow with total comfort.",
      fr: "Nous organisons les transferts entre aéroports et hébergements, les véhicules privés, les chauffeurs, les guides locaux et tous les services nécessaires pour que votre voyage se déroule en toute sérénité.",
      es: "Organizamos traslados entre aeropuertos y alojamientos, vehículos privados, chóferes, guías locales y todos los servicios necesarios para que tu viaje transcurra con total comodidad.",
    },
  },
  {
    key: "custom",
    icon: Compass,
    title: {
      en: "A fully bespoke trip",
      fr: "Un voyage entièrement sur-mesure",
      es: "Un viaje completamente a medida",
    },
    desc: {
      en: "If you are looking for a unique experience, we will design an itinerary tailored to your interests, dates, budget and travel style. Whether you cross the south of Morocco as a couple, a family, with friends or a group, we will craft a proposal thought entirely around you.",
      fr: "Si vous recherchez une expérience unique, nous concevrons un itinéraire adapté à vos intérêts, dates, budget et façon de voyager. Que vous parcouriez le sud du Maroc en couple, en famille, entre amis ou en groupe, nous préparerons une proposition pensée exclusivement pour vous.",
      es: "Si buscas una experiencia única, diseñaremos un itinerario adaptado a tus intereses, fechas, presupuesto y forma de viajar. Tanto si recorres el sur de Marruecos en pareja, en familia, con amigos o en grupo, prepararemos una propuesta pensada exclusivamente para ti.",
    },
  },
];

const BookingInfoModal = ({ open, lang, onClose, onOpenPlanner }) => {
  // Escape-key close + lock the underlying page scroll while open.
  useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", handleKey);
    };
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4 py-6 md:py-10"
      role="dialog"
      aria-modal="true"
      aria-labelledby="booking-info-modal-title"
      data-testid="booking-info-modal"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label={pick(COPY.close, lang)}
        onClick={onClose}
        className="absolute inset-0 w-full h-full bg-[#14100E]/85 backdrop-blur-sm"
        data-testid="booking-info-modal-backdrop"
      />

      {/* Panel */}
      <div
        className="relative w-full max-w-3xl max-h-full overflow-y-auto bg-[#1A1513] text-[#FDFBF7] border border-[#D4A373]/25 shadow-[0_28px_60px_-16px_rgba(0,0,0,0.65)]"
        data-testid="booking-info-modal-panel"
      >
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          aria-label={pick(COPY.close, lang)}
          className="absolute top-4 right-4 md:top-6 md:right-6 z-10 inline-flex items-center justify-center w-9 h-9 text-[#FDFBF7]/70 hover:text-[#FDFBF7] hover:bg-[#FDFBF7]/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A373]"
          data-testid="booking-info-modal-close"
        >
          <X className="w-4 h-4" strokeWidth={1.6} />
        </button>

        <div className="px-6 md:px-10 pt-10 md:pt-12 pb-8 md:pb-10 border-b border-[#FDFBF7]/10">
          <p className="overline !text-[#D4A373]">
            {pick(COPY.overline, lang)}
          </p>
          <h2
            id="booking-info-modal-title"
            className="mt-3 font-serif-x text-3xl md:text-4xl tracking-tight leading-[1.05]"
            data-testid="booking-info-modal-title"
          >
            {pick(COPY.title, lang)}
          </h2>
          <p className="mt-5 text-sm md:text-base text-[#FDFBF7]/80 leading-relaxed">
            {pick(COPY.intro_lead, lang)}
          </p>
          <p className="mt-3 text-sm md:text-base text-[#FDFBF7]/80 leading-relaxed">
            {pick(COPY.intro_response, lang)}
          </p>
          <p className="mt-5 text-[10px] md:text-[11px] tracking-[0.28em] uppercase text-[#D4A373]">
            {pick(COPY.transition, lang)}
          </p>
        </div>

        {/* Request families */}
        <ul
          className="px-6 md:px-10 py-6 md:py-8 divide-y divide-[#FDFBF7]/10"
          data-testid="booking-info-modal-list"
        >
          {REQUEST_TYPES.map((r) => {
            const Icon = r.icon;
            return (
              <li
                key={r.key}
                className="flex gap-4 py-4 md:py-5"
                data-testid={`booking-info-modal-item-${r.key}`}
              >
                <div className="shrink-0 w-10 h-10 rounded-full border border-[#D4A373]/50 bg-[#C16542]/15 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-[#D4A373]" strokeWidth={1.6} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-serif-x text-lg md:text-xl leading-snug text-[#FDFBF7]">
                    {pick(r.title, lang)}
                  </h3>
                  <p className="mt-1 text-sm text-[#FDFBF7]/75 leading-relaxed">
                    {pick(r.desc, lang)}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>

        {/* Outro + primary CTA */}
        <div className="px-6 md:px-10 pt-4 pb-8 md:pb-10 border-t border-[#FDFBF7]/10 flex flex-col md:flex-row items-start md:items-center gap-5 md:gap-6">
          <p className="flex-1 text-sm text-[#FDFBF7]/70 leading-relaxed">
            {pick(COPY.outro, lang)}
          </p>
          <button
            type="button"
            onClick={() => {
              onClose?.();
              onOpenPlanner?.();
            }}
            className="group inline-flex items-center justify-center gap-2 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-6 md:px-7 py-3.5 text-[10px] md:text-[11px] tracking-[0.28em] uppercase transition-colors whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A373]"
            data-testid="booking-info-modal-open-planner"
          >
            {pick(COPY.cta, lang)}
            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.6} />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BookingInfoModal;
